<template>
  <div>
    <!-- stacked composition bar with tappable segments (only categories with revenue) -->
    <div class="flex gap-[3px] h-[34px] rounded-[10px] overflow-hidden">
      <div
        v-for="seg of barSegments"
        :key="seg.key"
        class="flex items-center justify-center cursor-pointer transition-opacity duration-200"
        :style="{
          flex: `${seg.value} 1 0%`,
          minWidth: '14px',
          background: seg.color,
          opacity: active && active !== seg.key ? 0.28 : 1,
        }"
        @click="pick(seg.key)">
        <span
          v-if="seg.value / total > 0.16"
          class="text-[11px] font-bold text-white"
          style="text-shadow: 0 1px 2px rgba(0,0,0,.2)">
          {{ Math.round((seg.value / total) * 100) }}%
        </span>
      </div>
    </div>

    <!-- legend / detail — swaps to the tapped segment -->
    <div v-if="picked" class="flex items-center gap-2 mt-2.5 text-xs">
      <span class="w-[9px] h-[9px] rounded-[3px] shrink-0" :style="{background: picked.color}"/>
      <span class="font-bold">{{ picked.label }}</span>
      <span class="opacity-85">
        {{ formatCurrency(picked.value) }}<template v-if="picked.count"> · {{ picked.count }} {{ picked.countHint }}</template>
      </span>
      <span class="mr-auto opacity-60 cursor-pointer text-[11px]" @click="clear">✕</span>
    </div>
    <div v-else class="flex flex-wrap gap-x-3.5 gap-y-1 mt-2.5">
      <div
        v-for="seg of segments"
        :key="seg.key"
        class="flex items-center gap-1.5 text-[10.5px] opacity-90 cursor-pointer"
        @click="pick(seg.key)">
        <span class="w-2 h-2 rounded-[3px]" :style="{background: seg.color}"/>
        {{ seg.label }}
        <b class="font-bold">{{ formatCurrency(seg.value) }}</b>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, ref} from 'vue';
import {formatCurrency} from './analytics-charts';

export interface IMixSegment {
  key: string;
  label: string;
  value: number;
  color: string;
  count?: number;
  countHint?: string;
}

const props = defineProps<{ segments: IMixSegment[] }>();

const emit = defineEmits<{ pick: [segment: IMixSegment | null] }>();

const active = ref<string | null>(null);

// the legend lists every category (₪0 included); the bar draws only real revenue
const barSegments = computed(() => props.segments.filter(s => s.value > 0));

const total = computed(() => props.segments.reduce((s, m) => s + m.value, 0) || 1);
const picked = computed(() => props.segments.find(s => s.key === active.value) ?? null);

const pick = (key: string) => {
  active.value = active.value === key ? null : key;
  emit('pick', picked.value);
};

const clear = () => {
  active.value = null;
  emit('pick', null);
};
</script>
